import { z } from "zod";
import { optional } from "./appointments";
import { membershipStatus, recordId } from "./contracts";
import { blankAsNull, searchTextUpTo, unset } from "./fields";
import { emailAddress } from "./organization-settings";

export const WORKER_CONTRACT_PAGE_SIZE_MAX = 100;
export const WORKER_CONTRACT_SEARCH_MAX_LENGTH = 200;
export const WORKER_CONTRACT_SERVICES_MAX = 200;
export const WORKER_CONTRACT_NOTES_MAX = 5000;

const searchText = searchTextUpTo(WORKER_CONTRACT_SEARCH_MAX_LENGTH);

const trimmedSearch = z.preprocess((value) => (typeof value === "string" ? unset(value.trim()) : value), searchText.optional());

export const workerContractPerson = z.object({
  id: recordId,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  avatar: z.string().nullable(),
});
export type WorkerContractPerson = z.infer<typeof workerContractPerson>;

export const employerKind = z.enum(["vendor", "subcontractor"]);
export type EmployerKind = z.infer<typeof employerKind>;

export const workerContractEmployer = z.object({ id: recordId, name: z.string(), kind: employerKind });
export type WorkerContractEmployer = z.infer<typeof workerContractEmployer>;

export const workerContractService = z.object({ id: recordId, name: z.string() });
export type WorkerContractService = z.infer<typeof workerContractService>;

export const workerContractStatus = membershipStatus;
export type WorkerContractStatus = z.infer<typeof workerContractStatus>;

export const workerContract = z.object({
  id: recordId,
  person: workerContractPerson,
  employer: workerContractEmployer,
  status: workerContractStatus,
  services: z.array(workerContractService),
  notes: z.string().nullable(),
  paymentTermsDays: z.number().int(),
  createdAt: z.string(),
});
export type WorkerContract = z.infer<typeof workerContract>;

const actingOrganization = optional(recordId.toLowerCase());

export const workerContractReadQuery = z.object({ actingOrganization });
export type WorkerContractReadQuery = z.infer<typeof workerContractReadQuery>;

export const workerContractListQuery = workerContractReadQuery.extend({
  status: optional(workerContractStatus),
  q: trimmedSearch,
  service: trimmedSearch,
  sort: z.preprocess(unset, z.enum(["name", "email"]).default("name")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("asc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(WORKER_CONTRACT_PAGE_SIZE_MAX).default(25)),
});
export type WorkerContractListQuery = z.infer<typeof workerContractListQuery>;

export const workerContractPage = z.object({ items: z.array(workerContract), total: z.number().int().nonnegative() });
export type WorkerContractPage = z.infer<typeof workerContractPage>;

const serviceIds = z.array(recordId.toLowerCase()).max(WORKER_CONTRACT_SERVICES_MAX);

export const workerContractAddRequest = z.object({ actingOrganization, email: emailAddress, services: serviceIds.default([]) }).strict();
export type WorkerContractAddRequest = { actingOrganization?: string; email: string; services?: string[] };

export const settableWorkerContractStatus = z.enum(["active", "suspended"]);
export type SettableWorkerContractStatus = z.infer<typeof settableWorkerContractStatus>;

export const workerContractStatusRequest = z.object({ status: settableWorkerContractStatus }).strict();
export type WorkerContractStatusRequest = z.infer<typeof workerContractStatusRequest>;

export const workerContractServicesRequest = z.object({ services: serviceIds }).strict();
export type WorkerContractServicesRequest = z.infer<typeof workerContractServicesRequest>;

export const workerContractNotesRequest = z.object({ notes: z.preprocess(blankAsNull, z.string().max(WORKER_CONTRACT_NOTES_MAX).nullable()) }).strict();
export type WorkerContractNotesRequest = { notes: string | null };
